import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import toast from "react-hot-toast";
import { Box, TextField, Button, Typography, Link, CircularProgress } from "@mui/material";
import client from "../../api/client";
import AuthShell from "./AuthShell";

const RESEND_SECONDS = 60;

/**
 * Registration email confirmation. Reached after /register with the email in
 * router state; the user enters the 6-digit code we emailed, then the org is
 * queued for superadmin approval.
 */
const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState(location.state?.email || "");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!email || !/^\d{6}$/.test(code)) {
      toast.error("Enter the 6-digit code sent to your email");
      return;
    }
    setSubmitting(true);
    try {
      await client.post("/auth/verify-otp", {
        email: email.trim().toLowerCase(),
        code,
        purpose: "register",
      });
      toast.success("Email verified — your registration is pending approval");
      navigate("/pending", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.error || "Verification failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || !email) return;
    try {
      await client.post("/auth/resend-otp", {
        email: email.trim().toLowerCase(),
        purpose: "register",
      });
      toast.success("A new code has been sent");
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) setCooldown(RESEND_SECONDS);
      toast.error(err?.response?.data?.error || "Could not resend the code");
    }
  };

  return (
    <AuthShell>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "secondary.main" }}>
          Verify your email
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Enter the 6-digit code we sent{email ? ` to ${email}` : ""}
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleVerify} noValidate>
        {!location.state?.email && (
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            autoComplete="email"
            sx={{ mb: 2 }}
          />
        )}
        <TextField
          label="Verification code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          fullWidth
          required
          autoFocus
          inputProps={{ inputMode: "numeric", maxLength: 6, style: { letterSpacing: "0.4em", fontWeight: 600 } }}
          sx={{ mb: 2 }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="secondary"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ py: 1.25 }}
        >
          {submitting ? "Verifying…" : "Verify email"}
        </Button>
      </Box>

      <Typography variant="body2" sx={{ textAlign: "center", mt: 3, color: "text.secondary" }}>
        Didn't get it?{" "}
        <Link
          component="button"
          type="button"
          onClick={handleResend}
          disabled={cooldown > 0}
          underline="hover"
          sx={{ fontWeight: 600, verticalAlign: "baseline" }}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </Link>
        {"  ·  "}
        <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: 600 }}>
          Back to sign in
        </Link>
      </Typography>
    </AuthShell>
  );
};

export default VerifyEmailPage;
